'use client';

/**
 * Currency catalog context.
 *
 * <Money> needs two facts about a currency — its symbol and how many minor-unit
 * digits it has — and both already live in the `currencies` table. Hard-coding
 * them in the component means a new venue's currency needs a code change and a
 * deploy before its amounts render properly.
 *
 * This fetches the catalog ONCE per session and hands it to <Money> through
 * context. Deliberately non-blocking: the catalog is an ENRICHMENT, never a
 * prerequisite. If it hasn't loaded, or the request failed, or the component is
 * rendering server-side or in a unit test, `useCurrencyCatalog()` returns null
 * and Money falls back to its built-in formatting. Money must keep working with
 * no provider at all — a money component that renders nothing until a network
 * call lands is worse than one that renders a slightly plainer number.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';

import { listCurrencies, type Currency } from '@/lib/api/admin-currencies';

export type CurrencyCatalog = ReadonlyMap<string, Currency>;

const Ctx = React.createContext<CurrencyCatalog | null>(null);

export function CurrencyCatalogProvider({ children }: { children: React.ReactNode }) {
  const q = useQuery({
    queryKey: ['currency-catalog'],
    queryFn: ({ signal }) => listCurrencies(signal),
    // Reference data: it changes when someone seeds a currency, not intraday.
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: 1,
  });

  const map = React.useMemo<CurrencyCatalog | null>(() => {
    const rows = q.data?.data;
    if (!rows || rows.length === 0) {return null;}
    return new Map(rows.map((c) => [c.id.toUpperCase(), c]));
  }, [q.data]);

  return <Ctx.Provider value={map}>{children}</Ctx.Provider>;
}

/** The catalog, or null when it isn't available. Never throws — see the note above. */
export function useCurrencyCatalog(): CurrencyCatalog | null {
  return React.useContext(Ctx);
}

/** All known currencies, sorted by code — for pickers. Empty when unavailable. */
export function useCurrencyOptions(): Currency[] {
  const cat = useCurrencyCatalog();
  return React.useMemo(
    () => (cat ? [...cat.values()].sort((a, b) => a.id.localeCompare(b.id)) : []),
    [cat],
  );
}
